import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import sfx from "../utils/sound";
import { getStickerById } from "../data/stickers";
import { GAMES as ALL_GAMES, getGameById } from "../data/games";
import { getProductById } from "../data/products";
import { trackVisit, trackEvent } from "../utils/analytics";

const StoreContext = createContext(null);

const LS_KEY = "kidz-store-v1";
export const STARTING_COINS = 500;

const CLASSIC_11 = new Set([
  "spin",
  "cups",
  "memory",
  "balloon",
  "scratch",
  "tap",
  "inflate",
  "whack",
  "stack",
  "cookie",
  "reflex",
]);

const emptyState = () => ({
  name: "",
  dob: "",
  avatar: "🐻",
  language: "",
  notificationsOn: true,
  coins: STARTING_COINS,
  cart: [],
  wishlist: [],
  orders: [],
  lastDailyClaim: null,
  streakDays: 0,
  soundOn: true,
  gameStats: { wheelSpins: 0, cupsWon: 0, cupsLost: 0, coinsEarned: 0 },
  gamesWon: {},
  stickers: [],
  categoryBought: {},
  playSeconds: 0,
  totalPlaySeconds: 0,
  playDate: null, // YYYY-MM-DD — resets playSeconds when the day rolls over
  playSnoozeAt: 0,
  playCapped: false,
  achievements: {
    perfectGolf: false,
    reflexMaster: false,
    cookieBigRun: false,
    sharpshooter: false,
    nightOwl: false,
    earlyBird: false,
    claimedMonday: 0,
    claimDaysThisWeek: [], // day-of-week ints (0=Sun..6=Sat)
    claimWeekKey: null, // ISO week key used to reset claimDaysThisWeek
    // Brain quiz perfects
    mathQuizPerfect: false,
    triviaPerfect: false,
    wordProbPerfect: false,
    numberCountPerfect: false,
    multiplyStreak: false,
    // 67 Booth tracking
    sixSevenPlays: 0,
    sixSevenMega: false,
    sixSevenSpent: 0,
  },
  // 67 Booth voice preset ("girl1" | "girl2" | "boy1" | "boy2"). Persisted so
  // the picked voice survives reloads. Playback reads this via sfx.setVoice()
  // wired inside StoreProvider on hydrate + change.
  boothVoice: "girl1",
  // Prefer 2-player hot-seat mode for supported games (Tic-Tac-Toe, Snakes & Ladders).
  multiplayer: false,
  // Notification center: newest first. Keep only the most recent 40.
  notifications: [],
});

const todayStr = () => new Date().toISOString().slice(0, 10);
const yesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const loadState = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    return {
      ...emptyState(),
      ...parsed,
      gameStats: { ...emptyState().gameStats, ...(parsed.gameStats || {}) },
      gamesWon: { ...(parsed.gamesWon || {}) },
      stickers: [...(parsed.stickers || [])],
    };
  } catch {
    return emptyState();
  }
};

// Compute new stickers to award based on the incoming updated state.
const nextStickers = (curr) => {
  const owned = new Set(curr.stickers);
  const newlyEarned = [];
  const grant = (id) => {
    if (!owned.has(id)) {
      owned.add(id);
      newlyEarned.push(id);
    }
  };
  const a = curr.achievements || {};
  // First win
  if (Object.keys(curr.gamesWon).length >= 1) grant("first-win");
  // Coin milestones
  if (curr.gameStats.coinsEarned >= 500) grant("coin-collector");
  if (curr.gameStats.coinsEarned >= 2000) grant("coin-hoarder");
  if (curr.gameStats.coinsEarned >= 10000) grant("coin-millionaire");
  // Different games won
  const uniqueGames = Object.keys(curr.gamesWon).length;
  if (uniqueGames >= 5) grant("game-hopper");
  const classicWon = Object.keys(curr.gamesWon).filter((g) =>
    CLASSIC_11.has(g)
  ).length;
  if (classicWon >= CLASSIC_11.size) grant("games-master");
  if (uniqueGames >= ALL_GAMES.length) grant("arcade-legend");
  // Streaks
  if (curr.streakDays >= 3) grant("streak-3");
  if (curr.streakDays >= 7) grant("streak-7");
  if (curr.streakDays >= 14) grant("streak-14");
  if (curr.streakDays >= 30) grant("streak-30");
  // Orders
  if (curr.orders.length >= 1) grant("big-spender");
  if (curr.orders.length >= 5) grant("loyal-customer");
  if (curr.orders.length >= 10) grant("vip-shopper");
  if (curr.orders.some((o) => o.total >= 500)) grant("mega-order");
  // Wishlist
  if ((curr.wishlist || []).length >= 10) grant("wishlist-wizard");
  // Category purchases
  const cat = curr.categoryBought || {};
  if ((cat.toys || 0) >= 5) grant("toy-titan");
  if ((cat.candy || 0) >= 5) grant("sweet-tooth");
  if ((cat.books || 0) >= 5) grant("book-nerd");
  if ((cat.plushies || 0) >= 5) grant("plushie-parent");
  if ((cat.art || 0) >= 5) grant("art-aficionado");
  if ((cat.clothes || 0) >= 5) grant("fashionista");
  // Rare gameplay
  if (a.perfectGolf) grant("perfect-aim");
  if (a.reflexMaster) grant("reflex-master");
  if (a.cookieBigRun) grant("cookie-monster");
  if (a.sharpshooter) grant("sharpshooter");
  // Brain quizzes
  if (a.mathQuizPerfect) grant("math-whiz");
  if (a.triviaPerfect) grant("trivia-genius");
  if (a.wordProbPerfect) grant("word-wizard");
  if (a.numberCountPerfect) grant("count-champ");
  if (a.multiplyStreak) grant("multiply-mach");
  const brainWonIds = ["mathquiz", "count", "multiply", "wordprob", "trivia"].filter(
    (id) => curr.gamesWon[id]
  );
  if (brainWonIds.length >= 5) grant("brain-boss");
  if (curr.gamesWon.ttt && curr.gamesWon.snakes) grant("board-master");
  if ((curr.gamesWon.ttt || 0) >= 3) grant("ttt-strategist");
  if (curr.gamesWon.snakes) grant("dice-daredevil");
  // 67 Booth
  if ((a.sixSevenPlays || 0) >= 1) grant("six-seven-fan");
  if (a.sixSevenMega) grant("six-seven-mega");
  if ((a.sixSevenSpent || 0) >= 300) grant("sound-splurger");
  // Weekly / timed
  const claimDays = new Set(a.claimDaysThisWeek || []);
  if (claimDays.size >= 4) grant("week-warrior");
  if (claimDays.has(6) && claimDays.has(0)) grant("weekend-champion");
  if ((a.claimedMonday || 0) >= 1) grant("monday-motivator");
  if (a.nightOwl) grant("night-owl");
  if (a.earlyBird) grant("early-bird");
  return { stickers: Array.from(owned), newlyEarned };
};

// Streak reward table: day 1 -> 100, day 2 -> 125, ... day 7+ -> 250.
export const streakGiftAmount = (streakDays) => {
  const base = 100;
  const capped = Math.max(0, Math.min(streakDays - 1, 6));
  return base + capped * 25;
};

export const StoreProvider = ({ children }) => {
  const [state, setState] = useState(loadState);
  const [bigWin, setBigWin] = useState(null); // { title, subtitle, emoji, coins, id }
  const winSeqRef = useRef(0);
  const prevStickersRef = useRef(state.stickers);

  // Reconcile stickers once on mount so conditions already satisfied by the loaded
  // localStorage (streakDays >= 14, wishlist length >= 10, past orders total >= 500…)
  // unlock immediately instead of waiting for the next mutating action.
  useEffect(() => {
    setState((s) => {
      const { stickers } = nextStickers(s);
      if (stickers.length === s.stickers.length) return s;
      return { ...s, stickers };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  }, [state]);

  // Ping the analytics endpoint once per app load, and re-ping only if the
  // kid captures their nickname / dob AFTER the first ping went out empty.
  const visitPingedRef = useRef(false);
  const hydratingRef = useRef(false);
  const initialPingHadNameRef = useRef(false);
  const lastPingedRef = useRef({ dob: "", avatar: "" });
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
      return;
    }
    if (!visitPingedRef.current) {
      visitPingedRef.current = true;
      initialPingHadNameRef.current = !!state.name;
      lastPingedRef.current = { dob: state.dob || "", avatar: state.avatar || "" };
      // First ping is the heartbeat — profile_update stays false so stale
      // localStorage never overwrites the canonical row's saved selections.
      trackVisit(state.name || "", state.dob || "", state.avatar || "");
      return;
    }
    const dobChanged = (state.dob || "") !== lastPingedRef.current.dob;
    const avatarChanged = (state.avatar || "") !== lastPingedRef.current.avatar;
    if (hydratingRef.current) {
      // The change came from hydrateFromServer, not the user — align our
      // "last pinged" snapshot so we don't fire a duplicate profile save.
      lastPingedRef.current = { dob: state.dob || "", avatar: state.avatar || "" };
      initialPingHadNameRef.current = initialPingHadNameRef.current || !!state.name;
      hydratingRef.current = false;
      return;
    }
    if ((!initialPingHadNameRef.current && state.name) || dobChanged || avatarChanged) {
      initialPingHadNameRef.current = true;
      lastPingedRef.current = { dob: state.dob || "", avatar: state.avatar || "" };
      // Any later change is a user-driven profile save — persist it.
      trackVisit(
        state.name || "",
        state.dob || "",
        state.avatar || "",
        { profileUpdate: true },
      );
    }
  }, [state.name, state.dob, state.avatar]);

  useEffect(() => {
    sfx.enabled = state.soundOn;
  }, [state.soundOn]);

  // Sticker unlock — pushes a notification into the notification center (no
  // sticky toast). Idempotent under StrictMode via the prev-sticker diff.
  useEffect(() => {
    const prev = new Set(prevStickersRef.current);
    const newlyEarned = state.stickers.filter((id) => !prev.has(id));
    if (newlyEarned.length > 0) {
      newlyEarned.forEach((id) => {
        const st = getStickerById(id);
        if (!st) return;
        sfx.fanfare();
        trackEvent("sticker_unlock", { meta: { sticker_id: id, name: st.name } });
        setState((s) => {
          const note = {
            id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            kind: "sticker",
            emoji: st.emoji,
            title: `Sticker unlocked: ${st.name}`,
            body: st.description || "Tap the bell to see all rewards.",
            at: Date.now(),
            read: false,
          };
          return {
            ...s,
            notifications: [note, ...(s.notifications || [])].slice(0, 40),
          };
        });
      });
    }
    prevStickersRef.current = state.stickers;
  }, [state.stickers]);

  // Applies a state update and then updates the sticker list based on the resulting state.
  // Pure — side effects for sticker unlock happen in the effect above.
  const applyWithStickers = (updater) => {
    setState((s) => {
      const updated = typeof updater === "function" ? updater(s) : updater;
      const { stickers } = nextStickers(updated);
      return { ...updated, stickers };
    });
  };

  const setName = useCallback((name) => setState((s) => ({ ...s, name })), []);
  // Explicit sticker grant, used for server-driven awards (weekly champion).
  const addSticker = useCallback((stickerId) => {
    if (!stickerId) return;
    setState((s) => {
      if ((s.stickers || []).includes(stickerId)) return s;
      return { ...s, stickers: [...(s.stickers || []), stickerId] };
    });
  }, []);
  const setDob = useCallback((dob) => setState((s) => ({ ...s, dob })), []);

  /**
   * Sign out and return to /welcome. Clears every trace of the current
   * anonymous session: the cafe state (coins, cart, stickers, name, dob),
   * the persistent visitor_id used for analytics, session-scoped playtime,
   * and referrer cookies. A brand-new visitor_id will be minted on the next
   * visit so the account cannot re-link automatically.
   */
  const signOut = useCallback(() => {
    try {
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem("cafe-visitor-id-v1");
      localStorage.removeItem("cafe-referrer-v1");
      localStorage.removeItem("cafe-session-start");
      localStorage.removeItem("kidz-fun-seen-v2");
      sessionStorage.clear();
    } catch {
      /* private mode — best effort */
    }
    setState(emptyState());
  }, []);
  const setAvatar = useCallback((avatar) => setState((s) => ({ ...s, avatar })), []);
  const setLanguage = useCallback((language) => setState((s) => ({ ...s, language })), []);
  const setNotificationsOn = useCallback(
    (on) => setState((s) => ({ ...s, notificationsOn: !!on })),
    []
  );

  /**
   * Hydrate the fields that are persisted server-side (avatar, name, dob)
   * from the canonical visitor row. Called after sign-in auto-merge and on
   * profile mount so the display stays consistent across devices.
   * Only fills fields the server actually has — never clobbers an active
   * local selection with a blank server value.
   */
  const hydrateFromServer = useCallback((serverDoc) => {
    if (!serverDoc) return;
    setState((s) => {
      const next = { ...s };
      if (serverDoc.avatar) next.avatar = serverDoc.avatar;
      if (serverDoc.last_name) next.name = serverDoc.last_name;
      if (serverDoc.dob) next.dob = serverDoc.dob;
      // Only signal the auto-visit effect when hydration will actually cause
      // a state change — otherwise React skips the re-render, the effect
      // never runs, and the flag would latch forever swallowing the next
      // genuine user save.
      const changed =
        next.avatar !== s.avatar ||
        next.name !== s.name ||
        next.dob !== s.dob;
      if (changed) hydratingRef.current = true;
      return next;
    });
  }, []);

  const addToCart = useCallback((product, qty = 1) => {
    setState((s) => {
      const existing = s.cart.find((c) => c.id === product.id);
      const cart = existing
        ? s.cart.map((c) => (c.id === product.id ? { ...c, qty: c.qty + qty } : c))
        : [...s.cart, { id: product.id, qty }];
      return { ...s, cart };
    });
  }, []);

  const updateQty = useCallback((id, qty) => {
    setState((s) => ({
      ...s,
      cart:
        qty <= 0
          ? s.cart.filter((c) => c.id !== id)
          : s.cart.map((c) => (c.id === id ? { ...c, qty } : c)),
    }));
  }, []);

  const removeFromCart = useCallback((id) => {
    setState((s) => ({ ...s, cart: s.cart.filter((c) => c.id !== id) }));
  }, []);

  const clearCart = useCallback(() => setState((s) => ({ ...s, cart: [] })), []);

  const toggleWishlist = useCallback((id) => {
    applyWithStickers((s) => ({
      ...s,
      wishlist: s.wishlist.includes(id)
        ? s.wishlist.filter((w) => w !== id)
        : [...s.wishlist, id],
    }));
  }, []);

  const checkout = useCallback((items, total) => {
    trackEvent("order_placed", {
      value: total,
      meta: {
        items_count: items.reduce((n, it) => n + (it.qty || 1), 0),
        distinct_items: items.length,
      },
    });
    const order = {
      id: `o-${Date.now()}`,
      date: new Date().toISOString(),
      items,
      total,
      coinsAfter: 0,
    };
    let created = order;
    applyWithStickers((s) => {
      const coinsAfter = Math.max(0, s.coins - total);
      created = { ...order, coinsAfter };
      // Tally purchases by product category so shopping-category stickers can trigger.
      const catBought = { ...(s.categoryBought || {}) };
      for (const it of items) {
        const prod = getProductById(it.id);
        if (prod && prod.category) {
          catBought[prod.category] = (catBought[prod.category] || 0) + (it.qty || 1);
        }
      }
      return {
        ...s,
        coins: coinsAfter,
        cart: [],
        orders: [created, ...s.orders],
        categoryBought: catBought,
        // Shopping counts as taking a real break — unlock games + reset the play counter.
        playCapped: false,
        playSeconds: 0,
        playSnoozeAt: 0,
      };
    });
    return created;
  }, []);

  // Detect which game is being played from the URL so we can record wins automatically.
  const detectGameId = () => {
    if (typeof window === "undefined") return null;
    const m = window.location.pathname.match(/^\/games\/([^/]+)/);
    return m ? m[1] : null;
  };

  const addCoins = useCallback((amount, meta = {}) => {
    // Explicit `meta.game: null` opts out of URL-based game detection so purchases
    // (e.g. 67 Booth) or multiplayer coin awards don't record a game win.
    const gameId =
      "game" in meta ? meta.game : amount > 0 ? detectGameId() : null;
    // Fire an analytics event for every coin motion so the admin dashboard
    // can show earned/spent totals per visitor.
    if (amount > 0) {
      trackEvent("coins_earned", {
        coins: amount,
        game_id: gameId || null,
        meta: { wheel: !!meta.wheel, break_kind: meta.break || null },
      });
    } else if (amount < 0) {
      trackEvent("coins_spent", {
        coins: Math.abs(amount),
        meta: { wheel: !!meta.wheel, source: meta.source || null },
      });
    }
    applyWithStickers((s) => {
      const gamesWon = { ...s.gamesWon };
      if (gameId && amount > 0) {
        gamesWon[gameId] = (gamesWon[gameId] || 0) + 1;
      }
      // Rare gameplay flags derived from the coin amount + gameId.
      const ach = { ...(s.achievements || {}) };
      if (gameId === "golf" && amount >= 200) ach.perfectGolf = true;
      if (gameId === "reflex" && amount >= 55) ach.reflexMaster = true;
      if (gameId === "cookie" && amount >= 200) ach.cookieBigRun = true;
      if (gameId === "sniper" && amount >= 150) ach.sharpshooter = true;
      // Brain quiz perfects — thresholds match a max-score payout per game.
      if (gameId === "mathquiz" && amount >= 90) ach.mathQuizPerfect = true;
      if (gameId === "trivia" && amount >= 108) ach.triviaPerfect = true;
      if (gameId === "wordprob" && amount >= 120) ach.wordProbPerfect = true;
      if (gameId === "count" && amount >= 100) ach.numberCountPerfect = true;
      if (gameId === "multiply" && amount >= 96) ach.multiplyStreak = true;
      return {
        ...s,
        coins: Math.max(0, s.coins + amount),
        gameStats: {
          ...s.gameStats,
          coinsEarned:
            amount > 0 ? s.gameStats.coinsEarned + amount : s.gameStats.coinsEarned,
          wheelSpins: meta.wheel ? s.gameStats.wheelSpins + 1 : s.gameStats.wheelSpins,
          cupsWon: meta.cupWin ? s.gameStats.cupsWon + 1 : s.gameStats.cupsWon,
          cupsLost: meta.cupLose ? s.gameStats.cupsLost + 1 : s.gameStats.cupsLost,
        },
        gamesWon,
        achievements: ach,
      };
    });
    // Offer a Brag Card for big wins (>= 50 coins from a game).
    if (amount >= 50 && gameId) {
      const meta2 = getGameById(gameId);
      winSeqRef.current += 1;
      setBigWin({
        id: `win-${winSeqRef.current}`,
        title: `+${amount} coins!`,
        subtitle: meta2 ? `Nailed ${meta2.name} on 67Cafe` : "Big win on 67Cafe",
        emoji: meta2?.emoji || "🏆",
        coins: amount,
      });
    }
  }, []);

  const dismissBigWin = useCallback(() => setBigWin(null), []);

  // Claim the daily gift; also advance the streak counter + record time-of-day flags.
  const claimDaily = useCallback(() => {
    let awarded = 0;
    let newStreak = 0;
    applyWithStickers((s) => {
      const today = todayStr();
      if (s.lastDailyClaim === today) return s;
      const yst = yesterdayStr();
      const streakDays = s.lastDailyClaim === yst ? s.streakDays + 1 : 1;
      const amount = streakGiftAmount(streakDays);
      awarded = amount;
      newStreak = streakDays;
      const now = new Date();
      const hour = now.getHours();
      const dow = now.getDay(); // 0=Sun..6=Sat
      const ach = { ...(s.achievements || {}) };
      // ISO-ish week key for the weekly-day-set reset
      const weekKey = `${now.getFullYear()}-${Math.floor(
        (Math.floor((now - new Date(now.getFullYear(), 0, 1)) / 86400000) + new Date(now.getFullYear(), 0, 1).getDay()) / 7
      )}`;
      const daysThisWeek = ach.claimWeekKey === weekKey ? [...(ach.claimDaysThisWeek || [])] : [];
      if (!daysThisWeek.includes(dow)) daysThisWeek.push(dow);
      ach.claimWeekKey = weekKey;
      ach.claimDaysThisWeek = daysThisWeek;
      if (dow === 1) ach.claimedMonday = (ach.claimedMonday || 0) + 1;
      if (hour >= 20 || hour < 5) ach.nightOwl = true;
      if (hour >= 5 && hour < 8) ach.earlyBird = true;
      return {
        ...s,
        coins: s.coins + amount,
        lastDailyClaim: today,
        streakDays,
        achievements: ach,
      };
    });
    return { amount: awarded, streakDays: newStreak };
  }, []);

  const canClaimDaily = state.lastDailyClaim !== todayStr();

  const setSoundOn = useCallback((on) => {
    setState((s) => ({ ...s, soundOn: on }));
  }, []);

  const setMultiplayer = useCallback((on) => {
    setState((s) => ({ ...s, multiplayer: !!on }));
  }, []);

  // Notification center actions — replaces toasts for anything the user should
  // be able to review later (sticker unlocks, big wins, rewards, milestones).
  const pushNotification = useCallback((note) => {
    setState((s) => {
      const entry = {
        id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        at: Date.now(),
        read: false,
        kind: "info",
        emoji: "🔔",
        title: "",
        body: "",
        ...note,
      };
      return { ...s, notifications: [entry, ...(s.notifications || [])].slice(0, 40) };
    });
  }, []);

  const markAllNotificationsRead = useCallback(() => {
    setState((s) => ({
      ...s,
      notifications: (s.notifications || []).map((n) => ({ ...n, read: true })),
    }));
  }, []);

  const clearNotifications = useCallback(() => {
    setState((s) => ({ ...s, notifications: [] }));
  }, []);

  const removeNotification = useCallback((id) => {
    setState((s) => ({
      ...s,
      notifications: (s.notifications || []).filter((n) => n.id !== id),
    }));
  }, []);

  // Playtime tracking — increments in short ticks while the kid is on /games/*.
  // `playSeconds` is the *daily* session counter (used for screen-time nudges),
  // `totalPlaySeconds` is the lifetime counter that survives midnight rollovers.
  const incrementPlay = useCallback((delta) => {
    setState((s) => {
      const today = todayStr();
      const nextTotal = (s.totalPlaySeconds || 0) + delta;
      // Roll the daily counter over at midnight.
      if (s.playDate !== today) {
        return {
          ...s,
          playDate: today,
          playSeconds: delta,
          totalPlaySeconds: nextTotal,
          playSnoozeAt: 0,
          playCapped: false,
        };
      }
      return {
        ...s,
        playSeconds: (s.playSeconds || 0) + delta,
        totalPlaySeconds: nextTotal,
      };
    });
  }, []);

  const resetPlaySession = useCallback(() => {
    setState((s) => ({ ...s, playSeconds: 0, playSnoozeAt: 0, playCapped: false }));
  }, []);

  const setPlaySnooze = useCallback((until) => {
    setState((s) => ({ ...s, playSnoozeAt: until }));
  }, []);

  const setPlayCapped = useCallback((v) => {
    setState((s) => ({ ...s, playCapped: !!v }));
  }, []);

  const resetAll = useCallback(() => setState(emptyState()), []);

  // 67 Booth — records a shout at a given tier (1..4) and its coin cost.
  // Deducts coins here (idempotent single write) and flags related achievements.
  const logSixSeven = useCallback((tier, cost) => {
    applyWithStickers((s) => {
      if (s.coins < cost) return s;
      const ach = { ...(s.achievements || {}) };
      ach.sixSevenPlays = (ach.sixSevenPlays || 0) + 1;
      ach.sixSevenSpent = (ach.sixSevenSpent || 0) + cost;
      if (tier >= 4) ach.sixSevenMega = true;
      return {
        ...s,
        coins: Math.max(0, s.coins - cost),
        achievements: ach,
      };
    });
  }, []);

  const setBoothVoice = useCallback((id) => {
    setState((s) => ({ ...s, boothVoice: id }));
    sfx.setVoice(id);
  }, []);

  // Push the persisted voice into the sfx singleton on hydrate/reload so
  // the very first shout uses the picked voice without waiting for a click.
  useEffect(() => {
    if (state.boothVoice) sfx.setVoice(state.boothVoice);
  }, [state.boothVoice]);

  return (
    <StoreContext.Provider
      value={{
        ...state,
        setName,
        signOut,
        hydrateFromServer,
        addSticker,
        setDob,
        setAvatar,
        setLanguage,
        setNotificationsOn,
        addToCart,
        updateQty,
        removeFromCart,
        clearCart,
        toggleWishlist,
        checkout,
        addCoins,
        claimDaily,
        canClaimDaily,
        setSoundOn,
        setMultiplayer,
        pushNotification,
        markAllNotificationsRead,
        clearNotifications,
        removeNotification,
        incrementPlay,
        resetPlaySession,
        setPlaySnooze,
        setPlayCapped,
        logSixSeven,
        setBoothVoice,
        resetAll,
        STARTING_COINS,
        bigWin,
        dismissBigWin,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used inside StoreProvider");
  return ctx;
};
